import React from "react";

import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";

interface EarningsMetricCardProps {
  title: string;
  value: string | number | undefined;
  icon?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

const EarningsMetricCard = ({
  title,
  value,
  icon,
  isLoading = false,
  className,
}: EarningsMetricCardProps) => {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? parseFloat(value.replace(/[^\d.-]/g, ""))
        : undefined;
  const isNonZero =
    Number.isFinite(numericValue as number) && (numericValue as number) !== 0;
  return (
    <div
      className={cn(
        "bg-[#f9f9f9] rounded-2xl p-4 w-full flex items-center gap-3",
        className,
      )}
    >
      <div
        className={cn(
          "relative rounded-[6px] size-8 flex-shrink-0 flex items-center justify-center transition-colors duration-300 ease-in-out",
          isNonZero ? "bg-[#def5f9]" : "bg-[#e8e8e8]",
        )}
      >
        <div
          className={cn(
            "absolute inset-0 rounded-[6px] border pointer-events-none transition-colors duration-300 ease-in-out",
            isNonZero ? "border-[#def5f9]" : "border-[#e8e8e8]",
          )}
          aria-hidden="true"
        />
        <div
          className={cn(
            "size-[18px] transition-all duration-300 ease-in-out",
            isNonZero
              ? "text-[#5ccedf] opacity-100"
              : "text-[#494949] opacity-80",
          )}
        >
          {icon}
        </div>
      </div>

      <Typography
        size="l2"
        className="text-[#767677] font-medium tracking-[-0.56px]"
      >
        {title}
      </Typography>

      <div className="ml-auto flex-shrink-0">
        {isLoading ? (
          <div className="h-8 w-16 rounded-md bg-muted animate-pulse" />
        ) : (
          <Typography
            size="h2"
            as="div"
            className="text-[#767677] font-semibold tracking-[-0.96px]"
          >
            {value ?? "—"}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default EarningsMetricCard;
